/* eslint-disable react/no-array-index-key */

import { ChevronDownIcon, ChevronUpIcon } from "icons";
import { unparse } from "papaparse";
import { useEffect, useMemo, useState } from "react";
import { Button as RAButton } from "react-aria-components";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import { Button, Empty, Text } from "ui-components";

import { countries } from "@/src/constants/conutries";
import { OrganizationJobClicksByCountryResponse } from "@/src/hooks/api/generated";

const predefinedColors = [
  "#FF6B6B",
  "#4ECDC4",
  "#556270",
  "#C7F464",
  "#FFA500",
  "#8A2BE2",
  "#FFD700",
];

export function ClicksByCountryInfo({
  organisationJobsClickData,
}: {
  organisationJobsClickData?: OrganizationJobClicksByCountryResponse;
}): JSX.Element {
  const [currentSort, setCurrentSort] = useState({
    key: "",
    direction: "ASC",
  });

  const [tableData, setTableData] = useState<
    OrganizationJobClicksByCountryResponse | undefined
  >(organisationJobsClickData);

  useEffect(() => {
    if (organisationJobsClickData) {
      setTableData(organisationJobsClickData);
    }
  }, [organisationJobsClickData]);

  const countryColorMap = useMemo(() => {
    const clicksByCountry = organisationJobsClickData?.clicksByCountry || [];

    const map = new Map<string, string>();
    clicksByCountry.forEach((item, index) => {
      let assignedColor: string;
      if (index < predefinedColors.length) {
        assignedColor = predefinedColors[index]!;
      } else {
        assignedColor = `#${Math.floor(Math.random() * 16777215)
          .toString(16)
          .padStart(6, "0")}`;
      }
      map.set(item.country, assignedColor);
    });
    return map;
  }, [organisationJobsClickData?.clicksByCountry]);

  const downloadClicksByCountryReport = (): void => {
    const csv = unparse(
      organisationJobsClickData?.clicksByCountry?.map((item) => ({
        Country: countries[item.country],
        Clicks: item.clicks,
      })) || []
    );
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "clicks-by-country-report.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const sortData = (key: "clicks" | "country"): void => {
    if (currentSort.direction === "DESC") {
      const sorted = tableData?.clicksByCountry.sort((a, b) =>
        key === "country"
          ? a.country.localeCompare(b.country)
          : a.clicks - b.clicks
      );
      setTableData({ clicksByCountry: sorted || [] });
      setCurrentSort({ key, direction: "ASC" });
    } else {
      const sorted = tableData?.clicksByCountry.sort((a, b) =>
        key === "country"
          ? b.country.localeCompare(a.country)
          : b.clicks - a.clicks
      );
      setTableData({ clicksByCountry: sorted || [] });
      setCurrentSort({ key, direction: "DESC" });
    }
  };

  return (
    <div className="w-full flex">
      <div className="w-1/2">
        <ResponsiveContainer width="100%" height={320}>
          <PieChart>
            <Pie
              data={organisationJobsClickData?.clicksByCountry}
              dataKey="clicks"
              fill="green"
            >
              {organisationJobsClickData?.clicksByCountry.map(
                (entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={countryColorMap.get(entry.country)}
                  />
                )
              )}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        {!!organisationJobsClickData?.clicksByCountry.length && (
          <Button
            className="mx-auto"
            onClick={() => downloadClicksByCountryReport()}
          >
            Download job country report
          </Button>
        )}
      </div>
      <div className="w-1/2">
        <div className="flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-[#D9D9D9] text-center">
                  <tr>
                    <th scope="col" className="py-5 text-center">
                      <RAButton
                        className="flex justify-center gap-x-2 mx-auto items-center"
                        onPress={() => sortData("country")}
                      >
                        <Text>Job Country </Text>
                        {currentSort.key === "country" &&
                        currentSort.direction === "ASC" ? (
                          <ChevronUpIcon className="w-4" />
                        ) : (
                          <ChevronDownIcon className="w-4" />
                        )}
                      </RAButton>
                    </th>
                    <th scope="col" className="py-5 text-center">
                      <RAButton
                        className="flex justify-center gap-x-2 mx-auto items-center"
                        onPress={() => sortData("clicks")}
                      >
                        <Text>Clicks on Apply </Text>
                        {currentSort.key === "clicks" &&
                        currentSort.direction === "ASC" ? (
                          <ChevronUpIcon className="w-4" />
                        ) : (
                          <ChevronDownIcon className="w-4" />
                        )}
                      </RAButton>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 text-center">
                  {organisationJobsClickData?.clicksByCountry.length === 0 && (
                    <tr>
                      <td colSpan={2} aria-label="no record found.">
                        <Empty text="No data found." />
                      </td>
                    </tr>
                  )}
                  {tableData?.clicksByCountry.map((item) => (
                    <tr key={item.country}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 font-medium text-gray-900 sm:pl-0 flex items-center">
                        <span
                          className="inline-block w-3 h-3 mr-2"
                          style={{
                            backgroundColor: countryColorMap.get(item.country),
                          }}
                        />
                        {countries[item.country]}
                      </td>
                      <td className="py-5">
                        <Text>{item.clicks}</Text>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
